import { create } from "zustand";

// Configuracion de la grilla de Playback multi-camara. Solo CONFIG (device,
// fecha, celdas, modo, foco). El estado runtime de cada celda (stream, cursor,
// pausa) vive local en PlaybackCell; la orquestacion (seek/play/pause) la hace
// PlaybackPage via refs imperativos. Restriccion del producto: todas las celdas
// son del MISMO dispositivo.

export type PlaybackMode = "sync" | "independent";

export interface PlaybackCellCfg {
  cellId: string;
  channel: number | null;
}

// count -> [cols, rows]. Layouts panoramicos (mejor para monitores 16:9).
export const PB_LAYOUTS: Record<number, [number, number]> = {
  1: [1, 1],
  2: [2, 1],
  4: [2, 2],
  6: [3, 2],
  8: [4, 2],
};
export const PB_COUNTS = [1, 2, 4, 6, 8];

function todayStr(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function buildCells(count: number, prev: PlaybackCellCfg[] = []): PlaybackCellCfg[] {
  return Array.from({ length: count }).map((_, i) => ({
    cellId: `pb-${i}`,
    channel: prev[i]?.channel ?? null,
  }));
}

interface PlaybackGridState {
  deviceId: number | null;
  date: string; // yyyy-MM-dd
  count: number;
  cells: PlaybackCellCfg[];
  mode: PlaybackMode;
  focusedCellId: string | null;

  setDevice: (id: number | null) => void;
  setDate: (d: string) => void;
  setCount: (n: number) => void;
  assignChannel: (cellId: string, deviceId: number, channel: number) => void;
  clearCell: (cellId: string) => void;
  clearAll: () => void;
  setMode: (m: PlaybackMode) => void;
  setFocused: (cellId: string | null) => void;
}

export const usePlaybackGridStore = create<PlaybackGridState>((set) => ({
  deviceId: null,
  date: todayStr(),
  count: 4,
  cells: buildCells(4),
  mode: "sync",
  focusedCellId: "pb-0",

  // Cambiar de dispositivo limpia todos los canales (la grilla es siempre de un
  // solo equipo).
  setDevice: (id) =>
    set((s) =>
      id === s.deviceId
        ? {}
        : { deviceId: id, cells: buildCells(s.count) },
    ),

  setDate: (d) => set({ date: d }),

  setCount: (n) =>
    set((s) => {
      const cells = buildCells(n, s.cells);
      const focused = cells.some((c) => c.cellId === s.focusedCellId)
        ? s.focusedCellId
        : cells[0]?.cellId ?? null;
      return { count: n, cells, focusedCellId: focused };
    }),

  // Drag-drop de un canal a una celda. Si el canal arrastrado es de un device
  // distinto al actual, se adopta ese device y se limpian las demas celdas
  // (misma restriccion de un solo equipo).
  assignChannel: (cellId, deviceId, channel) =>
    set((s) => {
      if (s.deviceId != null && s.deviceId !== deviceId) {
        const cells = s.cells.map((c) =>
          c.cellId === cellId ? { ...c, channel } : { ...c, channel: null },
        );
        return { deviceId, cells, focusedCellId: cellId };
      }
      const cells = s.cells.map((c) =>
        c.cellId === cellId ? { ...c, channel } : c,
      );
      return { deviceId, cells, focusedCellId: cellId };
    }),

  clearCell: (cellId) =>
    set((s) => ({
      cells: s.cells.map((c) =>
        c.cellId === cellId ? { ...c, channel: null } : c,
      ),
    })),

  clearAll: () => set((s) => ({ cells: buildCells(s.count) })),

  setMode: (m) => set({ mode: m }),

  setFocused: (cellId) => set({ focusedCellId: cellId }),
}));
